<template>
  <div class="d-comment-entry-report modal modal-lg active">
    <div class="overlay" />

    <div class="modal-content">
      <div class="modal-header">
        <h4>Report comment</h4>
        <a
          class="button button-yellow button-icon button-close-white button-close-modal"
          @click="dismiss"
        />
      </div>

      <div
        class="modal-body loadable"
        :class="{ 'loading': (isLoading || isProcessing) }"
      >
        <div
          v-if="(isLoading || isProcessing)"
          class="loading-content"
        >
          <DuiLoadingIndicator size="lg" />
          <p
            v-if="isProcessing"
            class="space-top-2em"
          >
            Submitting report...
          </p>
        </div>

        <div class="loaded-content">
          <p class="space-bottom-2em">
            Please indicate a reason why you would like to report
            this comment.
          </p>

          <form>
            <div class="form-group">
              <label for="parent-reason">Reason</label>

              <DuiDropdown
                v-model="selectedParentReason"
                name="parent-reason"
                :options="parentReasons"
                placeholder="Please select a reason"
              />
            </div>

            <div
              v-if="availableChildReasons"
              class="form-group"
            >
              <p>
                <strong>Please specify your reason</strong>
              </p>

              <DuiRadioGroup
                v-model="selectedChildReason"
                name="child-reason"
                :options="availableChildReasons"
              />
            </div>

            <div class="form-group">
              <label for="description">Further details</label>
              <textarea
                v-model="description"
                name="description"
                placeholder="Provide further details..."
              />
            </div>

            <div class="button-pair right">
              <button
                type="button"
                @click="dismiss"
              >
                Cancel
              </button>

              <button
                class="button-yellow"
                :disabled="!canSubmit"
                type="button"
                @click="submit"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import commentsService from '@d-services/comments-service/comments-service';
import { getErrorMessage } from '@d-shared/utils/errors';
import modalMixin from '@d-mixins/modal';

export default {
  mixins: [
    modalMixin,
  ],
  props: {
    comment: {
      type: Object,
      required: true,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book',
          'post',
          'user-review',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
  },
  data: () => ({
    childReasons: null,
    description: null,
    isLoading: false,
    isProcessing: false,
    parentReasons: null,
    selectedChildReason: null,
    selectedParentReason: null,
  }),
  computed: {
    availableChildReasons() {
      if (!this.selectedParentReason) {
        return null;
      }

      const displayedChildReasons = this.childReasons[this.selectedParentReason];
      if (!displayedChildReasons || !displayedChildReasons.length) {
        return null;
      }

      return displayedChildReasons;
    },

    canSubmit() {
      if (!this.selectedParentReason) {
        return false;
      }

      if (this.selectedParentReason && !this.availableChildReasons) {
        return true;
      }

      return !!(this.selectedParentReason && this.selectedChildReason);
    },
  },
  watch: {
    selectedParentReason: function() {
      this.$set(this, 'selectedChildReason', null);
    },
  },
  mounted() {
    this.loadReportReasons();
  },
  methods: {
    dismiss() {
      this.$emit('dismiss');
    },

    async loadReportReasons() {
      this.$set(this, 'isLoading', true);

      try {
        let reportReasons = await commentsService.getCommentReportReasons();
        this.$set(this, 'parentReasons', reportReasons.firstLevel);
        this.$set(this, 'childReasons', reportReasons.secondLevel);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },

    async submit() {
      if (!this.canSubmit) {
        return;
      }

      if (this.comment.is_reported === true) {
        return;
      }

      this.$set(this, 'isProcessing', true);

      try {
        switch(this.itemType) {
          case 'book':
            await commentsService.reportBookComment(
              this.itemSlug,
              this.comment.id,
              this.selectedParentReason,
              this.selectedChildReason,
              this.description,
            );
            break;
          case 'post':
            await commentsService.reportPostComment(
              this.itemSlug,
              this.comment.id,
              this.selectedParentReason,
              this.selectedChildReason,
              this.description,
            );
            break;
          case 'user-review':
            await commentsService.reportUserReviewComment(
              this.itemSlug,
              this.comment.id,
              this.selectedParentReason,
              this.selectedChildReason,
              this.description,
            );
            break;
        }

        this.$set(this.comment, 'is_reported', true);
        Vue.duiNotificationManager.success('Comment has been reported.');
        this.dismiss();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },
  },
};
</script>
